import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";

async function downloadCSV(name, rows, columns) {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const body = rows.map((r) => columns.map((c) => `"${String(r[c.key] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `${name}.csv`; a.click(); URL.revokeObjectURL(url);
}

export default function Reports() {
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  useEffect(() => {
    api.get("/dashboard/kpis").then((r)=>setKpis(r.data));
    api.get("/dashboard/charts").then((r)=>setCharts(r.data));
  }, []);

  const exportOverdue = async () => {
    const { data } = await api.get("/maintenance", { params: { filter: "overdue" } });
    if (!data.length) return toast.error("Nothing to export");
    downloadCSV("overdue_maintenance", data, [
      {key:"title",label:"Task"},{key:"asset_group",label:"Group"},
      {key:"frequency_text",label:"Frequency"},{key:"next_due_date",label:"Due"},
      {key:"priority",label:"Priority"},{key:"is_regulatory",label:"Regulatory"},{key:"status",label:"Status"},
    ]);
  };
  const exportCompliance = async () => {
    const { data } = await api.get("/compliance");
    if (!data.length) return toast.error("Nothing to export");
    downloadCSV("compliance_status", data, [
      {key:"category",label:"Category"},{key:"requirement",label:"Requirement"},
      {key:"requirement_type",label:"Type"},{key:"standard_ref",label:"Standard"},
      {key:"next_due_date",label:"Next due"},{key:"certificate_expiry_date",label:"Cert expiry"},
      {key:"verified",label:"Verified"},
    ]);
  };
  const exportAssets = async () => {
    const { data } = await api.get("/assets");
    downloadCSV("asset_register", data, [
      {key:"asset_group",label:"Group"},{key:"name",label:"Name"},{key:"location",label:"Location"},
      {key:"criticality",label:"Criticality"},{key:"service_frequency",label:"Frequency"},
      {key:"compliance_ref",label:"Compliance"},{key:"responsible_party",label:"Responsible"},
    ]);
  };

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">Exports</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Reports</h1>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200"><CardContent className="p-5">
          <h3 className="font-heading text-lg font-semibold">One-page Compliance PDF</h3>
          <p className="text-sm text-slate-600 mt-1">Professional summary suitable for insurers, council or auditors.</p>
          <Button className="mt-3" onClick={()=>window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/compliance.pdf`, "_blank")} data-testid="export-compliance-pdf"><Download className="w-4 h-4 mr-1"/>Download PDF</Button>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5">
          <h3 className="font-heading text-lg font-semibold">Overdue maintenance</h3>
          <p className="text-sm text-slate-600 mt-1">All tasks past their next-due date.</p>
          <Button className="mt-3" onClick={exportOverdue} data-testid="export-overdue"><Download className="w-4 h-4 mr-1"/>Export CSV</Button>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5">
          <h3 className="font-heading text-lg font-semibold">Compliance status</h3>
          <p className="text-sm text-slate-600 mt-1">Full compliance register with certificate expiries.</p>
          <Button className="mt-3" onClick={exportCompliance} data-testid="export-compliance"><Download className="w-4 h-4 mr-1"/>Export CSV</Button>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5">
          <h3 className="font-heading text-lg font-semibold">Asset register</h3>
          <p className="text-sm text-slate-600 mt-1">Every asset with criticality, frequency and responsibility.</p>
          <Button className="mt-3" onClick={exportAssets} data-testid="export-assets"><Download className="w-4 h-4 mr-1"/>Export CSV</Button>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5">
          <h3 className="font-heading text-lg font-semibold">Printable QR sheet</h3>
          <p className="text-sm text-slate-600 mt-1">All assets as a printable QR sheet — stick on each asset for scan-to-inspect.</p>
          <Button className="mt-3" onClick={()=>window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/qr-sheet.pdf`, "_blank")} data-testid="export-qr-sheet"><Download className="w-4 h-4 mr-1"/>Download PDF</Button>
        </CardContent></Card>
      </div>

      <Card className="border-slate-200"><CardContent className="p-5">
        <h3 className="font-heading text-lg font-semibold">Management summary</h3>
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
            <Row label="Overdue tasks" v={kpis.overdue} tone="text-red-700"/>
            <Row label="Due next 30d" v={kpis.due_30} tone="text-amber-700"/>
            <Row label="Regulatory due" v={kpis.regulatory_due} tone="text-red-700"/>
            <Row label="Critical defects" v={kpis.critical_defects_open} tone="text-red-700"/>
            <Row label="Open work orders" v={kpis.open_work_orders} tone="text-sky-700"/>
            <Row label="Completed this month" v={kpis.completed_this_month} tone="text-emerald-700"/>
            <Row label="Expiring certificates" v={kpis.expiring_certificates} tone="text-amber-700"/>
            <Row label="Annual spend" v={`$${Number(kpis.annual_expenditure||0).toLocaleString()}`} tone="text-slate-800"/>
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}

function Row({ label, v, tone }) {
  return (<div className="border border-slate-200 rounded-md p-3">
    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className={`mt-1 text-2xl font-heading font-bold ${tone||""}`}>{v ?? "—"}</div>
  </div>);
}
